import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Menu,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Upload as UploadIcon,
  AutoAwesome as AIIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Archive as ArchiveIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { dokumenteApi, vorlagenApi, adressenApi, textbausteineApi, llmApi } from '../services/api';
import type {
  GeneriertesDokument,
  GenerateDokumentRequest,
  Vorlage,
  Adresse,
  Textbaustein,
} from '../types/api';
import RichTextEditor from '../components/RichTextEditor';
import ArchivDialog from '../components/ArchivDialog';
import PortalShareDialog from '../components/PortalShareDialog';
import { marked } from 'marked';

// Hilfsfunktion: Konvertiert Newlines in HTML-Zeilenumbrüche für TipTap
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _convertNewlinesToHtml = (text: string): string => {
  if (!text) return '';

  // Debug-Ausgabe
  console.log('convertNewlinesToHtml - Input:', text.substring(0, 200));

  // Prüfe, ob der Text Newlines enthält aber keine paragraph tags - dann brauchen wir Konvertierung
  const hasNewlines = text.includes('\n');
  const hasParagraphTags = text.includes('<p>') && text.includes('</p>');

  // Wenn bereits korrekt formatiertes HTML mit p-Tags, direkt zurückgeben
  if (hasParagraphTags && !hasNewlines) {
    console.log('convertNewlinesToHtml - Already HTML, returning as-is');
    return text;
  }

  // Wenn es Newlines gibt (plain text oder gemischter Inhalt), konvertieren
  if (hasNewlines) {
    console.log('convertNewlinesToHtml - Converting newlines to HTML');
    const lines = text.split('\n');
    let html = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (!line) {
        // Leere Zeile (aber nicht die letzte) -> füge Leerzeile ein
        if (i < lines.length - 1) {
          html += '<p></p>';
        }
      } else if (line.startsWith('<') && (line.includes('</') || line.endsWith('>'))) {
        // Zeile enthält bereits HTML-Tags -> direkt übernehmen ohne zusätzliches Wrapping
        console.log('convertNewlinesToHtml - Line already contains HTML:', line.substring(0, 50));
        html += line;
      } else {
        // Plain text Zeile -> als Absatz wrappen
        html += '<p>' + line + '</p>';
      }
    }

    console.log('convertNewlinesToHtml - Output:', html.substring(0, 200));
    return html;
  }

  // Fallback: Text ohne Newlines und ohne HTML
  console.log('convertNewlinesToHtml - Wrapping in single paragraph');
  return '<p>' + text + '</p>';
};

const Dokumente: React.FC = () => {
  const navigate = useNavigate();
  const [dokumente, setDokumente] = useState<GeneriertesDokument[]>([]);
  const [vorlagen, setVorlagen] = useState<Vorlage[]>([]);
  const [adressen, setAdressen] = useState<Adresse[]>([]);
  const [textbausteine, setTextbausteine] = useState<Textbaustein[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [inhalt, setInhalt] = useState('');
  const [fusszeile, setFusszeile] = useState('');
  const [llmGenerating, setLlmGenerating] = useState(false);
  const [llmPrompt, setLlmPrompt] = useState('');
  const [formData, setFormData] = useState<GenerateDokumentRequest>({
    vorlageID: 0,
    dokumentTyp: 'Brief',
    betreff: '',
  });
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedDokumentId, setSelectedDokumentId] = useState<number | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [archivDialogOpen, setArchivDialogOpen] = useState(false);
  const [archivDokumentId, setArchivDokumentId] = useState<number | null>(null);
  const [archivDokumentTitle, setArchivDokumentTitle] = useState('');
  const [portalShareDialogOpen, setPortalShareDialogOpen] = useState(false);
  const [portalShareDokumentId, setPortalShareDokumentId] = useState<number | null>(null);
  const [portalShareDokumentTitle, setPortalShareDokumentTitle] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hilfsfunktion: Markdown in HTML konvertieren (nur inline-Formatierung)
  const convertMarkdownToHtml = (markdown: string): string => {
    try {
      // Verarbeite nur Inline-Markdown (fett, kursiv, etc.)
      // ohne Block-Level-Tags wie <p>, <ul>, <li> zu erzeugen

      // Teile den Text in Zeilen auf
      const lines = markdown.split('\n');

      // Verarbeite jede Zeile mit parseInline
      const processedLines = lines.map(line => {
        if (line.trim() === '') {
          return '<br>'; // Leere Zeilen als Zeilenumbruch
        }
        return marked.parseInline(line) as string;
      });

      // Füge die Zeilen wieder zusammen
      return processedLines.join('<br>');
    } catch (error) {
      console.error('Fehler bei Markdown-Konvertierung:', error);
      return markdown; // Fallback: Original-Text zurückgeben
    }
  };

  const loadData = async (searchTerm?: string) => {
    try {
      setLoading(true);
      setError(null);
      // Verwende den übergebenen searchTerm, falls vorhanden, sonst den State
      const search = searchTerm !== undefined ? searchTerm : searchQuery;
      const [dokResponse, vorlageResponse, adressenResponse, textbausteineResponse] =
        await Promise.all([
          dokumenteApi.getAll({ take: 100, search: search || undefined }),
          vorlagenApi.getAll(),
          adressenApi.getAll({ take: 500 }),
          textbausteineApi.getAll(),
        ]);
      setDokumente(dokResponse.data);
      setVorlagen(vorlageResponse.data);
      setAdressen(adressenResponse.data);
      setTextbausteine(textbausteineResponse.data);
    } catch (err: any) {
      console.error('Dokumente-Fehler:', err);
      setError(
        err.response?.data?.message || 'Fehler beim Laden der Dokumente'
      );
    } finally {
      setLoading(false);
    }
  };

  const _handleOpenDialog = () => {
    setFormData({
      vorlageID: 0,
      dokumentTyp: 'Brief',
      betreff: '',
    });
    setInhalt('');
    setLlmPrompt('');
    setDialogTab(0);
    setDialogOpen(true);
  };
  void _handleOpenDialog; // Suppress unused warning

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setInhalt('');
    setFusszeile('');
    setDialogTab(0);
  };

  const handleInsertTextbaustein = async (textbaustein: Textbaustein) => {
    try {
      const response = await textbausteineApi.getContent(textbaustein.bausteinID);
      const content = response.data.content || '';
      console.log('Textbaustein geladen:', { bausteinID: textbaustein.bausteinID, contentLength: content.length, content });

      if (!content) {
        alert('Textbaustein hat keinen Inhalt');
        return;
      }

      setInhalt(prev => {
        const newContent = prev + (prev ? '\n\n' : '') + content;
        console.log('Neuer Inhalt:', newContent);
        return newContent;
      });

      // Wechsle zum Inhalt-Tab, damit der eingefügte Text sichtbar ist
      setDialogTab(0);
    } catch (err: any) {
      console.error('Textbaustein Error:', err);
      alert('Fehler beim Laden des Textbausteins: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleLlmGenerate = async () => {
    if (!llmPrompt.trim()) {
      alert('Bitte geben Sie eine Beschreibung für die KI ein');
      return;
    }

    try {
      setLlmGenerating(true);
      const currentPrompt = llmPrompt;
      setLlmPrompt(''); // Leere sofort das Eingabefeld

      const response = await llmApi.generate({ prompt: currentPrompt });
      const generatedText = response.data.text || '';

      console.log('LLM generiert:', { prompt: currentPrompt, generatedText });

      // Konvertiere Markdown in HTML für den Editor
      const htmlContent = convertMarkdownToHtml(generatedText);

      setInhalt(prev => {
        const newContent = prev + (prev ? '<br><br>' : '') + htmlContent;
        return newContent;
      });

      // Kurzes Delay, dann zum Inhalt-Tab wechseln
      setTimeout(() => setDialogTab(0), 100);
    } catch (err: any) {
      console.error('LLM Error:', err);
      alert('Fehler bei der KI-Generierung: ' + (err.response?.data?.message || err.message));
    } finally {
      setLlmGenerating(false);
    }
  };

  const handleGenerate = async () => {
    try {
      if (!formData.vorlageID) {
        alert('Bitte wählen Sie eine Vorlage aus');
        return;
      }

      const requestData = {
        ...formData,
        inhalt: inhalt || undefined,
        fusszeile: fusszeile || undefined,
      };

      const response = await dokumenteApi.generate(requestData);
      setDokumente([response.data, ...dokumente]);
      handleCloseDialog();
    } catch (err: any) {
      alert('Fehler beim Generieren des Dokuments');
    }
  };

  const handleDelete = async (dokumentId: number) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;

    try {
      await dokumenteApi.delete(dokumentId);
      setDokumente(dokumente.filter((d) => d.dokumentID !== dokumentId));
    } catch (err: any) {
      alert('Fehler beim Löschen des Dokuments');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validierung der Dateiformate
    const allowedExtensions = ['.docx', '.txt', '.md', '.odt', '.pdf', '.xlsx', '.xls'];
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      alert(`Dateiformat ${extension} wird nicht unterstützt. Erlaubt sind: ${allowedExtensions.join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploadLoading(true);
    try {
      await dokumenteApi.upload(file);
      await loadData();
      alert('Dokument wurde erfolgreich hochgeladen');
    } catch (err: any) {
      console.error('Upload-Fehler:', err);
      alert('Fehler beim Hochladen des Dokuments');
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (dokumentId: number, format?: string) => {
    try {
      const response = await dokumenteApi.download(dokumentId, format);

      // Dateinamen aus dem Content-Disposition Header extrahieren
      const contentDisposition = response.headers['content-disposition'];

      // Bestimme die Dateiendung basierend auf dem Format
      const extension = format ? `.${format}` : '.pdf';
      let fileName = `dokument_${dokumentId}${extension}`; // Fallback mit korrekter Endung

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=(['"]?)(.+?)\1/);
        if (fileNameMatch && fileNameMatch.length > 2) {
          fileName = fileNameMatch[2];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Menü schließen
      setDownloadMenuAnchor(null);
    } catch (err: any) {
      alert('Fehler beim Herunterladen des Dokuments');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Entwurf':
        return 'warning';
      case 'Versendet':
        return 'success';
      case 'Archiviert':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".docx,.txt,.md,.odt,.pdf,.xlsx,.xls"
        onChange={handleFileUpload}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Dokumente</Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={() => loadData()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadLoading}
          >
            {uploadLoading ? 'Wird hochgeladen...' : 'Dokument hochladen'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dokumente/neu')}
          >
            Neues Dokument
          </Button>
        </Box>
      </Box>

      <Box mb={3} display="flex" gap={2}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Dokumente durchsuchen (Betreff, Zusammenfassung, Text)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              loadData();
            }
          }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => loadData()}
          sx={{ minWidth: '120px' }}
        >
          Suchen
        </Button>
        {searchQuery && (
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={() => {
              setSearchQuery('');
              // Direkt mit leerem String laden
              loadData('');
            }}
            sx={{ minWidth: '120px' }}
          >
            Zurücksetzen
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Typ</TableCell>
              <TableCell>Betreff</TableCell>
              <TableCell>Adresse</TableCell>
              <TableCell>Zusammenfassung</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell>Versendet am</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dokumente.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">
                    Keine Dokumente vorhanden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dokumente.map((dokument) => (
                <TableRow
                  key={dokument.dokumentID}
                  hover
                  onClick={() => navigate(`/dokumente/${dokument.dokumentID}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DescriptionIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {dokument.dokumentTyp}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{dokument.betreff}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {dokument.adresseName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    {dokument.zusammenfassung ? (
                      <Tooltip title={dokument.zusammenfassung}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {dokument.zusammenfassung}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={dokument.status}
                      color={getStatusColor(dokument.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(dokument.erstelltAm).toLocaleString('de-DE')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {dokument.versendetAm
                        ? new Date(dokument.versendetAm).toLocaleString('de-DE')
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/dokumente/${dokument.dokumentID}`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Herunterladen">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setDownloadMenuAnchor(e.currentTarget);
                            setSelectedDokumentId(dokument.dokumentID);
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Für Portal freigeben">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => {
                            setPortalShareDokumentId(dokument.dokumentID);
                            setPortalShareDokumentTitle(dokument.betreff);
                            setPortalShareDialogOpen(true);
                          }}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="In Paperless archivieren">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => {
                            setArchivDokumentId(dokument.dokumentID);
                            setArchivDokumentTitle(dokument.betreff);
                            setArchivDialogOpen(true);
                          }}
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(dokument.dokumentID)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog für Neues Dokument */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Neues Dokument generieren</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Vorlage</InputLabel>
              <Select
                value={formData.vorlageID}
                label="Vorlage"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    vorlageID: Number(e.target.value),
                  })
                }
              >
                <MenuItem value={0}>
                  <em>Bitte wählen...</em>
                </MenuItem>
                {vorlagen.map((vorlage) => (
                  <MenuItem key={vorlage.vorlageID} value={vorlage.vorlageID}>
                    {vorlage.vorlageName} ({vorlage.vorlageTyp})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Adresse (Optional)</InputLabel>
              <Select
                value={formData.adresseID || ''}
                label="Adresse (Optional)"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    adresseID: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
              >
                <MenuItem value="">
                  <em>Keine Adresse</em>
                </MenuItem>
                {adressen.map((adresse) => (
                  <MenuItem key={adresse.adresseID} value={adresse.adresseID}>
                    {adresse.vollstaendigerName}
                    {adresse.firma && ` (${adresse.firma})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Betreff"
              value={formData.betreff}
              onChange={(e) =>
                setFormData({ ...formData, betreff: e.target.value })
              }
            />

            <FormControl fullWidth>
              <InputLabel>Dokumenttyp</InputLabel>
              <Select
                value={formData.dokumentTyp}
                label="Dokumenttyp"
                onChange={(e) =>
                  setFormData({ ...formData, dokumentTyp: e.target.value })
                }
              >
                <MenuItem value="Brief">Brief</MenuItem>
                <MenuItem value="Email">E-Mail</MenuItem>
                <MenuItem value="Excel">Excel</MenuItem>
              </Select>
            </FormControl>

            {/* Tabs für Inhalt / Textbausteine / KI */}
            <Paper variant="outlined">
              <Tabs value={dialogTab} onChange={(_, newValue) => setDialogTab(newValue)}>
                <Tab label="Inhalt" />
                <Tab label="Textbausteine" />
                <Tab label="KI-Assistent" />
              </Tabs>

              <Box p={2}>
                {/* Tab 0: Manueller Inhalt */}
                {dialogTab === 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom mb={1}>
                      Dokumentinhalt:
                    </Typography>
                    <RichTextEditor
                      content={inhalt}
                      onChange={setInhalt}
                      minHeight="500px"
                      maxHeight="700px"
                    />
                  </Box>
                )}

                {/* Tab 1: Textbausteine */}
                {dialogTab === 1 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Klicken Sie auf einen Textbaustein, um ihn einzufügen:
                    </Typography>
                    <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {textbausteine.map((tb) => (
                        <ListItem key={tb.bausteinID} disablePadding>
                          <ListItemButton onClick={() => handleInsertTextbaustein(tb)}>
                            <ListItemText
                              primary={tb.bausteinName}
                              secondary={tb.kurzerText || tb.kategorie}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                      {textbausteine.length === 0 && (
                        <ListItem>
                          <ListItemText secondary="Keine Textbausteine verfügbar" />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                )}

                {/* Tab 2: KI-Assistent */}
                {dialogTab === 2 && (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Alert severity="info">
                      Beschreiben Sie, welchen Text die KI generieren soll.
                    </Alert>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Beschreibung für KI"
                      value={llmPrompt}
                      onChange={(e) => setLlmPrompt(e.target.value)}
                      placeholder="z.B. 'Schreibe eine höfliche Absage für eine Bewerbung'"
                    />
                    <Button
                      variant="outlined"
                      startIcon={llmGenerating ? <CircularProgress size={20} /> : <AIIcon />}
                      onClick={handleLlmGenerate}
                      disabled={llmGenerating}
                    >
                      {llmGenerating ? 'Generiere...' : 'Mit KI generieren'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>

            <TextField
              fullWidth
              label="Fußzeile / Grußformel"
              value={fusszeile}
              onChange={(e) => setFusszeile(e.target.value)}
              multiline
              rows={2}
              placeholder="z.B. Mit freundlichen Grüßen"
              helperText="Text, der am Ende des Dokuments erscheint"
            />

            {inhalt && (
              <Alert severity="success">
                Aktueller Inhalt: {inhalt.length} Zeichen
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button variant="contained" onClick={handleGenerate}>
            Dokument generieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Format Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem onClick={() => selectedDokumentId && handleDownload(selectedDokumentId)}>
          Original-Format
        </MenuItem>
        <MenuItem onClick={() => selectedDokumentId && handleDownload(selectedDokumentId, 'pdf')}>
          PDF
        </MenuItem>
        <MenuItem onClick={() => selectedDokumentId && handleDownload(selectedDokumentId, 'docx')}>
          DOCX
        </MenuItem>
        <MenuItem onClick={() => selectedDokumentId && handleDownload(selectedDokumentId, 'xlsx')}>
          Excel (XLSX)
        </MenuItem>
        <MenuItem onClick={() => selectedDokumentId && handleDownload(selectedDokumentId, 'md')}>
          Markdown
        </MenuItem>
        <MenuItem onClick={() => selectedDokumentId && handleDownload(selectedDokumentId, 'txt')}>
          Text
        </MenuItem>
      </Menu>

      {/* Archivierungs-Dialog */}
      <ArchivDialog
        open={archivDialogOpen}
        onClose={() => setArchivDialogOpen(false)}
        sourceType="document"
        sourceId={archivDokumentId || 0}
        defaultTitle={archivDokumentTitle}
        onSuccess={(deleted) => {
          if (archivDokumentId) {
            if (deleted) {
              // Wenn das Dokument gelöscht wurde, aus der Liste entfernen
              setDokumente(dokumente.filter((d) => d.dokumentID !== archivDokumentId));
            } else {
              // Wenn nur archiviert (nicht gelöscht), Status in der Liste aktualisieren
              setDokumente(dokumente.map((d) =>
                d.dokumentID === archivDokumentId
                  ? { ...d, status: 'Archiviert' }
                  : d
              ));
            }
          }
        }}
      />

      {/* Portal-Freigabe-Dialog */}
      <PortalShareDialog
        open={portalShareDialogOpen}
        onClose={() => setPortalShareDialogOpen(false)}
        dokumentId={portalShareDokumentId || 0}
        dokumentTitel={portalShareDokumentTitle}
        onSuccess={() => {
          // Optional: Erfolgsmeldung oder Refresh
        }}
      />
    </Box>
  );
};

export default Dokumente;
